import { create } from 'zustand';
import type { CanvasDirective, CanvasOverlay, CanvasView } from '../types';
import { useTableViewStore } from './tableViewStore';
import { findingsDirectiveToSearch } from '../components/catalog/findings/findingFilters';
import { historyDirectiveToSearch } from '../components/changes/historyFilters';
import { attributesDirectiveToSearch } from '../components/catalog/attributes/attributeSort';
import { rowSearch } from '../components/catalog/table/rowParam';

/** Translate a directive's params into the target view's URL query string (no leading
 *  `?`), for the URL-driven views. Each view owns its own encoder (the inverse of the
 *  `parse*` its component reads with), so the mapping is 1:1 with no store hop. Entities
 *  is store-backed (tableViewStore) and profile takes no params, so both yield '' — a
 *  bare nav. (SC-2766 wired findings; SC-2768 adds history + attributes.) */
function directiveToSearch(directive: CanvasDirective): string {
  switch (directive.view) {
    case 'findings':
      return findingsDirectiveToSearch(directive);
    case 'history':
      return historyDirectiveToSearch(directive);
    case 'attributes':
      return attributesDirectiveToSearch(directive);
    default:
      return '';
  }
}

// --- Workspace canvas — the active-view store (Canvas P0, SC-2653) ------------
//
// Owns only the FRAME-level state of the three-panel workspace: which view is
// active, whether a detail overlay is layered over it, and (for `source: 'agent'`
// navigations) the provenance banner intent. Per-view PARAM state stays in its own
// store — the entities view keeps `tableViewStore` — and `applyCanvasDirective`
// FORWARDS to it rather than duplicating (composition, not duplication).
//
// Keyed by `catalogId` (same discipline as `tableViewStore` / `agentChatStore`), so
// each catalog remembers its active view; `reset()` is wired into
// `clearSessionState` (SC-2461) so a new identity can't inherit the previous one's
// view/overlay.
//
// P0 is INVISIBLE: nothing renders this store yet (the active view is still
// route-derived via `useMatch`). It exists to prove the plumbing — the seam the
// nav/canvas/chat shell (P2+) and the BE `open_view` swap (P6) plug onto.

/** The store-side overlay record: the wire `CanvasOverlay` shape plus an optional
 *  display `label` — the line the opening view already renders for the object
 *  (finding summary; the entity's id IS its identifier). Captured at open time so
 *  the chat's `canvas_context` snapshot (SC-2853) can name what's open without a
 *  lookup. Best-effort: agent-/URL-opened overlays may carry no label, and the
 *  wire type stays label-free (`CanvasOverlay` is assignable into this). */
export type WorkspaceOverlay =
  | (Exclude<CanvasOverlay, null> & { label?: string })
  | RowOverlay
  | null;

/** SC-3517 — the entity drawer opened by the row's Mongo `_id` (History's "Open
 *  product"), where `{ kind: 'entity' }` is opened by SKU. FE-ONLY: it is not a
 *  `CanvasOverlay` and must never reach the BE as one — `canvas_context` is parsed
 *  leniently, so one unknown overlay kind silently nulls the WHOLE snapshot.
 *  `sku` + `label` are filled once the row loads, so the chat snapshot can send it
 *  as the entity ref the agent knows (see `canvasContext.toContextRef`). */
export interface RowOverlay {
  kind: 'row';
  id: string;
  sku?: string;
  label?: string;
}

export interface WorkspaceSlice {
  /** The active canvas view. */
  activeView: CanvasView;
  /** A detail overlay layered over the active view, or `null` for none. */
  overlay: WorkspaceOverlay;
  /** Set when an AGENT directive navigated to the current (non-`entities`) view —
   *  the frame-level "⤳ from chat" provenance the ProvenanceBanner renders (P4,
   *  SC-2663). `null` = clean view: a user navigation, a cleared banner, OR an agent
   *  directive onto `entities` (which delegates provenance to its table caption, so
   *  the two don't double up). Mirrors the table's `narration` gate, lifted to the
   *  frame. `source` is always `'agent'` when set — a user nav is `null`. */
  provenance: { source: 'user' | 'agent'; narration: string } | null;
}

export const EMPTY_WORKSPACE_SLICE: WorkspaceSlice = {
  activeView: 'entities',
  overlay: null,
  provenance: null,
};

/** A store→URL navigation REQUEST (Canvas P4, SC-2663). `applyCanvasDirective` — the
 *  agent seam — bumps this so `CatalogLayout` can drive the router to the requested
 *  view; `navigate` (the URL→store mirror) never touches it, so a user/URL nav can't
 *  re-trigger a router nav (no loop). `seq` is a monotonic tiebreaker: two directives
 *  to the SAME view are still distinct requests, and the layout detects "a new nav
 *  happened" by the seq advancing rather than by diffing the (lagging, reactive)
 *  active view — which sidesteps the mount/load race entirely. */
type NavRequest = {
  catalogId: string;
  view: CanvasView;
  seq: number;
  /** The target view's URL query string (no leading `?`), for URL-driven views whose param
   *  state lives in the URL rather than a sibling store (findings today — SC-2766; history /
   *  attributes next — SC-2768). `undefined` for a store-backed view (entities → tableViewStore)
   *  or a bare nav. CatalogLayout appends it when navigating; it also lets a same-view refine
   *  (findings→findings with new filters) still drive a nav, since the search differs. */
  search?: string;
};

interface WorkspaceStore {
  byCatalog: Record<string, WorkspaceSlice>;
  /** The last agent-driven navigation request; see `NavRequest`. `null` until the
   *  first `applyCanvasDirective`. Global (one active catalog at a time); the layout
   *  gates on `catalogId` before navigating. */
  lastNav: NavRequest | null;
  /** User navigation (a nav click): switch to `view`, clean — no provenance, and
   *  dismiss any overlay that belonged to the view we're leaving. */
  navigate: (catalogId: string, view: CanvasView) => void;
  /** THE SEAM. Every agent producer (the BE `open_view` tool, or its DEV `/open`
   *  stub) routes here. Sets the frame (view + provenance + overlay), FORWARDS an
   *  entities directive's params to `tableViewStore.applyDirective` (the table keeps
   *  doing its own job, this store just points at it), and bumps `lastNav` so
   *  `CatalogLayout` drives the router to the requested view (store→URL). */
  applyCanvasDirective: (catalogId: string, directive: CanvasDirective) => void;
  /** Open a detail overlay over the active view (a cell/badge/row click). Callers
   *  that render a display line for the object pass it as `label` (best-effort —
   *  see {@link WorkspaceOverlay}). Views whose overlay open/close state lives
   *  elsewhere (the Findings view's `?finding=` param, the Attributes drawer's
   *  local state) MIRROR it here through this same setter, so the slice is the
   *  one canonical "what detail is open" record the chat snapshot reads. */
  openOverlay: (catalogId: string, overlay: Exclude<WorkspaceOverlay, null>) => void;
  /** SC-3517 — History's "Open product": switch to the table with the drawer open on
   *  row `rowId` (its Mongo `_id`). Sets view + overlay ATOMICALLY, like a directive,
   *  so CatalogLayout's URL→store mirror finds the view already `entities` and
   *  doesn't wipe the overlay on arrival; bumps `lastNav` with `?row=` so the router
   *  follows and the URL survives a reload / share. Not a `CanvasDirective`: a row
   *  overlay is FE-only (see {@link RowOverlay}). */
  openRow: (catalogId: string, rowId: string) => void;
  /** Dismiss the overlay (Esc / scrim / close). */
  closeOverlay: (catalogId: string) => void;
  /** The provenance banner's "Clear" — drop the "from chat" intent, keep the view. */
  clearProvenance: (catalogId: string) => void;
  /** Wipe ALL per-catalog workspace state on an identity change (logout / login)
   *  via `clearSessionState` (SC-2461), mirroring `tableViewStore.reset`. */
  reset: () => void;
}

function getSlice(state: WorkspaceStore, catalogId: string): WorkspaceSlice {
  return state.byCatalog[catalogId] ?? EMPTY_WORKSPACE_SLICE;
}

function withSlice(
  state: WorkspaceStore,
  catalogId: string,
  patch: Partial<WorkspaceSlice>,
): Pick<WorkspaceStore, 'byCatalog'> {
  return {
    byCatalog: {
      ...state.byCatalog,
      [catalogId]: { ...getSlice(state, catalogId), ...patch },
    },
  };
}

export const useWorkspaceStore = create<WorkspaceStore>((set) => ({
  byCatalog: {},
  lastNav: null,

  navigate: (catalogId, view) =>
    set((state) =>
      // A user-driven view switch is a clean slate: no provenance, and any overlay
      // from the previous view is stale (its content doesn't belong to `view`).
      withSlice(state, catalogId, { activeView: view, overlay: null, provenance: null }),
    ),

  applyCanvasDirective: (catalogId, directive) => {
    // Forward BEFORE the frame update (a plain call into the sibling store — no
    // nested `set`). Only the entities view has a param target in P0; other views
    // will read their params off the slice as they're built out (P1+).
    if (directive.params?.view === 'entities') {
      const { query, mode, narration } = directive.params.table;
      useTableViewStore.getState().applyDirective(catalogId, query, mode, narration);
    }
    set((state) => ({
      ...withSlice(state, catalogId, {
        activeView: directive.view,
        // A view switch dismisses a stale overlay unless the directive names a new
        // one (`undefined` ⇒ close; an explicit overlay ⇒ open it).
        overlay: directive.overlay ?? null,
        // Record the frame-level "⤳ from chat" provenance ONLY for agent navigations
        // to a NON-entities view. The `entities` view delegates its provenance to the
        // table caption (`tableViewStore.narration`, rendered in-view by TableTab):
        // an agent `get_table_slice` is a within-view FILTER, not a cross-view nav, so
        // recording frame provenance too would double-banner (SC-2663). A `source:
        // 'user'` directive — and any agent directive onto `entities` — clears the
        // frame provenance (arriving at the table makes a prior view's banner stale).
        provenance:
          directive.source === 'agent' && directive.view !== 'entities'
            ? { source: 'agent', narration: directive.narration }
            : null,
      }),
      // Request the store→URL nav (drained by CatalogLayout's E2). Bumped for EVERY
      // directive regardless of `source`: a CanvasDirective is a "show this view"
      // request, so it drives the router whether agent- or user-sourced — `source`
      // gates only the provenance banner (above), NOT whether we navigate. (Every
      // caller is agent-sourced today; a hypothetical user-sourced directive should
      // still navigate, just without a banner.) Covers the entities table so a
      // table-filter answer surfaces the table; E2 no-ops when the target URL already
      // matches, and `navigate()` (the URL→store mirror) never bumps this, so a
      // user/URL nav can't re-trigger a router nav.
      //
      // `search` carries the target view's filters/overlay as a URL query so CatalogLayout
      // can apply them by navigating — findings (SC-2766), history + attributes (SC-2768).
      // Each URL-driven view's params are encoded by `directiveToSearch`; entities is
      // store-backed (tableViewStore) and profile is a plain nav, so both encode to ''.
      lastNav: {
        catalogId,
        view: directive.view,
        seq: (state.lastNav?.seq ?? 0) + 1,
        // Empty (a bare nav, or a store-backed/no-param view) normalizes to undefined so
        // CatalogLayout treats it the same as "navigate with no query".
        search: directiveToSearch(directive) || undefined,
      },
    }));
  },

  openOverlay: (catalogId, overlay) =>
    set((state) => withSlice(state, catalogId, { overlay })),

  openRow: (catalogId, rowId) =>
    set((state) => ({
      ...withSlice(state, catalogId, {
        activeView: 'entities',
        overlay: { kind: 'row', id: rowId },
        provenance: null,
      }),
      lastNav: {
        catalogId,
        view: 'entities',
        seq: (state.lastNav?.seq ?? 0) + 1,
        search: rowSearch(rowId),
      },
    })),

  closeOverlay: (catalogId) =>
    set((state) => withSlice(state, catalogId, { overlay: null })),

  clearProvenance: (catalogId) =>
    set((state) => withSlice(state, catalogId, { provenance: null })),

  reset: () => set({ byCatalog: {}, lastNav: null }),
}));
